/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import RenderNode from "App/render/webgl/nodes/render_node";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import {mat4} from "gl-matrix";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";
import {Tools} from "App/environment/tools";
import ImageDataLoader from "App/utils/textures/image_data_loader";
import TextureNode from "App/render/webgl/nodes/texture_node";
import MaterialNode from "App/render/webgl/nodes/material_node";
import RenderOptionNode from "App/render/webgl/nodes/render_option_node";
import BatchesManagerNode from "App/render/webgl/nodes/batches_manager_node";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";
import IScene from "js_interface/src/scene_interface";

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @param {Object} aParentNode
 * @extends RenderNode
 * @constructor
 */
export default 
class RenderBinNode extends RenderNode {
    private _identityMatrixPrecalc: mat4;
    private _manipulatorTransformationInv: mat4;
    private _lightsEnabled: any = {};
    private _lightUniformsNames: any[];

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode) {
        super(aSceneAPI, aRenderGraph, aParentNode, null);

        this._identityMatrixPrecalc = mat4.create();
        this._manipulatorTransformationInv = mat4.create();

        // because of GC i.e. do not create strings dynamically during rendering
        this._lightUniformsNames = [];
        for (let i = 0; i < 5; i++) {
            let names = {
                position: "u_lights[" + i + "].position",
                diffuse: "u_lights[" + i + "].diffuse",
                specular: "u_lights[" + i + "].specular",
                enabled: "u_lights[" + i + "].enabled",
                fixed_pos: "u_lights[" + i + "].fixed_pos"
            };

            this._lightUniformsNames.push(names);
        }
    }

    /**
     * @param {string} typeName
     * @param {boolean} value
     */
    public setLightEnabled(typeName: string, value: boolean) {
        this._lightsEnabled[typeName] = value;
    }

    /**
     * @param {string} typeName
     */
    public isLightEnabled(typeName: string): boolean {
        let enabled = this._lightsEnabled[typeName];
        return enabled === undefined ? true : enabled;
    }

    /**
     * Set the model view projection method.
     *
     * @param {Pass} aRenderPass render pass with the active shader(s)
     */
    public setUniforms(aRenderPass: Pass) {
        /* jshint camelcase:false */
        let view = aRenderPass.view;
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let camera = view.getCamera();
        let controls = view.getControls();

        // set selection color
        gl.uniform3fv(shader.uniformLocations.u_selected_material_diffuse, this._sceneAPI.applicationModule.getSelectionColorRef());

        // send model view projection matrix to shader
        gl.uniform3fv(shader.uniformLocations.u_view_vector, camera.getCameraDirection());

        gl.uniformMatrix4fv(shader.uniformLocations.u_view_projection, false, camera.getViewProjectionMatrix());

        if (controls.getUseLogarithmicDepthBuffer()) {
            gl.uniform1f(shader.uniformLocations.u_log_depth_buf_FC, 2.0 / (Math.log(camera.getFarValue() + 1.0) / Math.LN2));
        } else {
            // this is used to sign that the logatihmic depth buffer is not used (without shader recompilation)
            gl.uniform1f(shader.uniformLocations.u_log_depth_buf_FC, -1.0);
        }

        let manipulatorTransformation = this._sceneAPI.activityModule.getCurrentManipulatorTransformation();
        if (manipulatorTransformation) {
            gl.uniformMatrix4fv(shader.uniformLocations.u_manipulator, false, manipulatorTransformation);

            if (this._sceneAPI.shadowsModule.getShadowsEnabled()) {
                gl.uniformMatrix4fv(shader.uniformLocations.u_manipulator_light, false, manipulatorTransformation);
                mat4.invert(this._manipulatorTransformationInv, manipulatorTransformation);
                gl.uniformMatrix4fv(shader.uniformLocations.u_manipulator_inv, false, this._manipulatorTransformationInv);
            } else {
                gl.uniformMatrix4fv(shader.uniformLocations.u_manipulator_light, false, this._identityMatrixPrecalc);
            }
        }

        gl.uniform1f(shader.uniformLocations.u_scale_invariant, 0.0);
    }

    public setUniformsForClippingPlane(aRenderPass: Pass) {
        /* jshint camelcase:false */
        let view = aRenderPass.view;
        let sceneAPI = view.getDocument().getSceneAPI();
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        gl.uniform1f(shader.uniformLocations.u_plane_active, sceneAPI.clippingPlaneModule.isEnabled() ? 1 : 0);
        gl.uniform4fv(shader.uniformLocations.u_plane, sceneAPI.clippingPlaneModule.getPlaneEquation());
        gl.uniform1f(shader.uniformLocations.u_highlight_cut, sceneAPI.clippingPlaneModule.isCutsVisible() ? 1 : 0);
    }

    public setUniformsForLighting(aRenderPass: Pass) {
        /* jshint camelcase:false */
        let view = aRenderPass.view;
        let sceneAPI = view.getDocument().getSceneAPI();
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let camera = view.getCamera();

        // send normal matrix for simple shading
        gl.uniformMatrix3fv(shader.uniformLocations.u_normal_mat, false, view.getCamera().getViewMatrix3IT());

        // send view matrix to shader
        gl.uniformMatrix4fv(shader.uniformLocations.u_view_matrix, false, view.getCamera().getViewMatrix());

        // set lights
        // TODO: create appropriate renderable in the view class (or in GLView?)
        gl.uniform1i(shader.uniformLocations.u_ambient_is_diffuse, sceneAPI.lightingModule.getAmbientEqualsDiffuse() ? 1 : 0);
        gl.uniform1f(shader.uniformLocations.u_minimal_diffuse_light, sceneAPI.lightingModule.getMinimalDiffuseLight());
        gl.uniform1f(shader.uniformLocations.u_ambient_light_intensity, sceneAPI.lightingModule.getAmbientLightIntensity());

        let lights = view.getLights();
        gl.uniform1i(shader.uniformLocations.u_lights_count, lights.length);
        for (let i = 0; i < lights.length; i++) {
            let light = lights[i];
            if (!this.isLightEnabled(light.getTypeName())) {
                continue;
            }
            gl.uniform4fv(shader.uniformLocations[this._lightUniformsNames[i].position], light.getPosition());
            gl.uniform3fv(shader.uniformLocations[this._lightUniformsNames[i].diffuse], light.getDiffuseColor());
            gl.uniform3fv(shader.uniformLocations[this._lightUniformsNames[i].specular], light.getSpecularColor());
            gl.uniform1f(shader.uniformLocations[this._lightUniformsNames[i].enabled], light.getEnabledValue());
            gl.uniform1f(shader.uniformLocations[this._lightUniformsNames[i].fixed_pos], light.getPositionFixed() ? 1.0 : 0.0);
        }

        // fog
        let lightingModule = view.getDocument().getSceneAPI().lightingModule;
        gl.uniform3fv(shader.uniformLocations.u_fogColor, lightingModule.getFog().getColor());
        gl.uniform1f(shader.uniformLocations.u_fogDensity, lightingModule.getFog().getDensity());
        gl.uniform1f(shader.uniformLocations.u_fogDistance, lightingModule.getFog().getDistance());

        // On mobiles we do not usually have more than 0-7 texture units
        // Use this only for normal lighting, not simple
        if (aRenderPass.getActiveShader().getLightingType() === 2) {
            let shadowsModule = this._sceneAPI.shadowsModule;

            // Set SSAO
            if (this._sceneAPI.lightingModule.getSSAO().isEnabled()) {
                let ssaoLayer = this._renderGraph.getLayer('ssao');
                let ssaoTexture = ssaoLayer.getTexture();
                if (ssaoTexture === null) {
                    ssaoTexture = Tools.getInstance().getPlaceholderTexture(gl, undefined);
                }

                gl.activeTexture(gl.TEXTURE7);
                gl.bindTexture(gl.TEXTURE_2D, ssaoTexture);
                gl.uniform1i(shader.uniformLocations.u_ssao_texture, 7);

                let canvas = this._context.getCanvas();
                let contentSize = canvas.getContentSize();
                let position = view.getPosition();
                let size = view.getSize();

                gl.uniform4f(shader.uniformLocations.u_view,
                    position[0] / contentSize[0],
                    position[1] / contentSize[1],
                    size[0] / contentSize[0],
                    size[1] / contentSize[1]
                );
            }

            // Set Shadow Map
            if (shadowsModule.getShadowMapEnabled()) {
                // Common
                gl.uniform1f(shader.uniformLocations.u_shadow_map_intensity, this._sceneAPI.lightingModule.getShadowIntensity());

                // Method specific
                let shadowMapType = shadowsModule.getShadowMapType();
                let shadowMapLayer = this._renderGraph.getLayer('shadowMaps');
                if (shadowMapType === 'pcf') {
                    let poissonTexture = shadowMapLayer.getPoissonTexture();
                    if (poissonTexture === null) {
                        poissonTexture = Tools.getInstance().getPlaceholderTexture(gl, undefined);
                    }

                    gl.activeTexture(gl.TEXTURE6);
                    gl.bindTexture(gl.TEXTURE_2D, poissonTexture);
                    gl.uniform1i(shader.uniformLocations.u_shadow_map_texture, 6);
                    gl.uniform2fv(shader.uniformLocations.u_shadow_map_size, shadowMapLayer.getPoissonTextureSize());
                } else if (shadowMapType === 'vsm') {
                    let shadowMapTexture = shadowMapLayer.getTexture();
                    if (shadowMapTexture === null) {
                        shadowMapTexture = Tools.getInstance().getPlaceholderTexture(gl, undefined);
                    }

                    gl.activeTexture(gl.TEXTURE6);
                    gl.bindTexture(gl.TEXTURE_2D, shadowMapTexture);
                    gl.uniform1i(shader.uniformLocations.u_shadow_map_texture, 6);

                    // Orthogonal projection matrix from light
                    let lightComponents = camera.getLightOrthogonalMatrixSeparate();
                    gl.uniformMatrix4fv(shader.uniformLocations.u_lightViewMatrix, false, lightComponents.view);
                    gl.uniformMatrix4fv(shader.uniformLocations.u_lightProjMatrix, false, lightComponents.projection);
                    gl.uniform1f(shader.uniformLocations.u_lightMaxLength, lightComponents.maxLength);
                    gl.uniform1f(shader.uniformLocations.u_lightBleeding, shadowsModule.getShadowMapLightBleeding());
                }
            }
        }
    }

    public childRemoved(aRemovedChild: RenderNode) {
        for (let i = 0; i < this.childnodes.length; i++) {
            if (this.childnodes[i].getUID() === aRemovedChild.getUID()) {
                this.childnodes[i].removed();
                this.childnodes.splice(i, 1);
            }
        }
    }

    public render(aRenderPass: Pass) {
        aRenderPass.getActiveShaderRenderable().enableAttributes();

        this._renderReturnVal = false;
        for (let i = 0; i !== this.childnodes.length; i++) {
            this._renderReturnVal = this.childnodes[i].render(aRenderPass) || this._renderReturnVal;
        }

        aRenderPass.getActiveShaderRenderable().disableAttributes();

        return this._renderReturnVal;
    }

    public notifySetTextureData(aUrl: string, aImageData: ImageDataLoader) {
        // this child nodes are texture nodes so we can check the uri and schedule replace the data
        for (let i = 0; i !== this.childnodes.length; i++) {
            let textureNode: any = this.childnodes[i];
            if (textureNode.getAppearance().texture) {
                let texture = textureNode.getAppearance().texture.get();
                if (texture.url[0] === aUrl) {
                    textureNode.setTextureData(aImageData);
                }
            }
        }
    }
}
